import {
  returnAllTradeDetailsFromGenericParams,
  returnOPLegFormThisTrade,
  returnRPLegFromThisTrade,
} from "./agGridColDefs/colDefGlobalHelpers";
import { StructureTypeCode } from "./interfaces and Enums/ENUMStructureTypeCode";
import { ExecutionStrategy } from "./interfaces and Enums/InterfaceExecutionStrategy";
import { ExecutionRequestQuoteLeg } from "./interfaces and Enums/InterfaceExecutionRequestQuoteLeg";
import tradeDataStore from "@/store/tradeDataStore";

const CURRENCY_SYMBOL_MAP: Record<string, string> = {
  GBP: "£",
  EUR: "€",
  JPY: "¥",
};

const PACK_STRUCTURE_TYPES: StructureTypeCode[] = [
  StructureTypeCode.PACK,
  StructureTypeCode.BNDPACK,
  StructureTypeCode.OPTPACK,
  StructureTypeCode.SWPPACK,
  StructureTypeCode.NOVPACK,
  StructureTypeCode.NPVPACK,
  StructureTypeCode.REPOPACK,
  StructureTypeCode.FUTPACK,
];

export function getCurrencySymbol(
  currencyCode: string | undefined,
): string {
  if (!currencyCode) return "$";
  // Symbols (£, €, ¥) sit tight against the number: £1,234.56
  // Currency codes (AUD, CAD) have a trailing space: AUD 1,234.56
  return CURRENCY_SYMBOL_MAP[currencyCode] || currencyCode + " ";
}

export function getCurrencyFromLeg(
  leg: ExecutionRequestQuoteLeg | undefined,
): string | undefined {
  if (!leg) return undefined;
  return leg.currencyLeg1 || leg.currency;
}

export function getSingleCurrencyFromTrade(
  thisTrade: ExecutionStrategy,
): string | undefined {
  const allCurrencies: string[] = [];

  // RP legs
  if (thisTrade.legs && thisTrade.legs.length > 0) {
    thisTrade.legs.forEach((leg) => {
      const currency = getCurrencyFromLeg(leg);
      if (currency) allCurrencies.push(currency);
    });
  }

  // OP legs (nested under counterparties)
  if (thisTrade.executionRequestCounterParties) {
    thisTrade.executionRequestCounterParties.forEach((ercp) => {
      ercp.legs.forEach((leg) => {
        const currency = getCurrencyFromLeg(leg);
        if (currency) allCurrencies.push(currency);
      });
    });
  }

  const uniqueCurrencies = [...new Set(allCurrencies)];
  if (uniqueCurrencies.length === 1) {
    return uniqueCurrencies[0];
  }
  return undefined;
}

export function determineNotationCurrencySymbol(
  fullTradeData: ExecutionStrategy,
  notation: string | undefined,
): string {
  if (!notation || notation !== "$") return notation || "$";

  // Trade-level unwind (NPV style)
  if (fullTradeData.unwind === true) {
    const singleCurrency = getSingleCurrencyFromTrade(fullTradeData);
    return getCurrencySymbol(singleCurrency);
  }

  // Single-currency pack
  if (
    fullTradeData.structureType &&
    PACK_STRUCTURE_TYPES.includes(fullTradeData.structureType)
  ) {
    const singleCurrency = getSingleCurrencyFromTrade(fullTradeData);
    if (singleCurrency) {
      return getCurrencySymbol(singleCurrency);
    }
  }

  return "$";
}

export function determineCurrencySymbol({
  thisTrade,
  thisLeg,
  isLeg,
  isER,
  isOutright,
  notation,
}: {
  thisTrade: ExecutionStrategy;
  thisLeg?: ExecutionRequestQuoteLeg;
  isLeg: boolean;
  isER: boolean;
  isOutright: boolean;
  notation?: string; // Optional: when provided, only resolve if notation is "$"
}): string {
  // Defensive check: only resolve currency when notation is "$"
  // When notation is omitted (cash/fee renderers), skip this check
  if (notation !== undefined && notation !== "$") return "$";

  // Block/Leg level: check if this specific leg is NPV or Recoupon
  if (isLeg || isOutright) {
    if (thisLeg?.unwind === "NPV" || thisLeg?.unwind === "Recoupon") {
      const currency = getCurrencyFromLeg(thisLeg);
      return getCurrencySymbol(currency);
    }
  }

  // ER level: check trade-level unwind OR single-currency pack
  if (isER && !isOutright) {
    if (thisTrade.unwind === true) {
      const singleCurrency = getSingleCurrencyFromTrade(thisTrade);
      return getCurrencySymbol(singleCurrency);
    }

    if (
      thisTrade.structureType &&
      PACK_STRUCTURE_TYPES.includes(thisTrade.structureType)
    ) {
      const singleCurrency = getSingleCurrencyFromTrade(thisTrade);
      if (singleCurrency) {
        return getCurrencySymbol(singleCurrency);
      }
    }
  }

  return "$";
}

/**
 * Unified notation resolver for AG Grid cell renderers.
 * Resolves "$" notation to the actual currency symbol based on trade/leg context.
 *
 * Rules:
 * - If notation is NOT "$", returns it as-is (e.g., "%", "bps", "$fr" stay unchanged)
 * - At Block/Leg/Fill level: checks if the leg is NPV or Recoupon → uses leg's currency
 * - At ER level: checks if trade.unwind === true OR single-currency pack → scans all legs
 * - Fills are treated as leg-level (they always belong to a leg)
 * - Default: returns "$" when no currency resolution applies
 *
 * @param notation - The raw notation string (e.g., "$", "%", "bps")
 * @param params - AG Grid cell params (used to determine level and find leg)
 * @param currentTrade - Optional trade object. If not provided, fetched from Zustand store.
 * @returns The resolved notation string (e.g., "£", "€", "AUD ", or original notation)
 */
export function resolveNotationFromParams(
  notation: string | undefined,
  params: any,
  currentTrade?: ExecutionStrategy,
): string | undefined {
  if (!notation || notation !== "$") return notation;

  const { fullTradeData, isLeg, isER, isOutright, isOp, isOrder, isFill } =
    returnAllTradeDetailsFromGenericParams(params);

  // Get trade from store if not provided
  const trade =
    currentTrade ||
    tradeDataStore
      .getState()
      .tradeData.find((t: ExecutionStrategy) => t.esid === fullTradeData?.esid);
  if (!trade) return notation;

  // Fills inherit leg-level currency context
  const effectiveIsLeg = isLeg || isFill;

  // Find the correct leg (OP or RP) using existing helpers
  const helperObject = {
    params,
    thisTrade: trade,
    isOp,
    isER,
    isOutright,
    isLeg: effectiveIsLeg,
    isOrder,
  };

  const thisLeg = isOp
    ? returnOPLegFormThisTrade(helperObject)
    : returnRPLegFromThisTrade(helperObject);

  return determineCurrencySymbol({
    thisTrade: trade,
    thisLeg,
    isLeg: effectiveIsLeg,
    isER,
    isOutright,
    notation,
  });
}
